import React, { useState } from 'react';
import { Alert, Upload, message, Modal, Tabs, Button, Space } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload';
import PageContainer from '@/components/PageContainer';
import { apiClient } from '@/api/client';
import type { UploadResponse } from '@/types/api';

const { Dragger } = Upload;

interface UploadTabProps {
  accept: string;
  hint: string;
  alertMessage: string;
  /** Upload function: call API, resolve on success, reject on error */
  uploadFn: (file: File, confirmOverwrite?: boolean) => Promise<UploadResponse>;
  /** Whether this endpoint supports overwrite confirmation (the AGC upload) */
  supportsOverwrite?: boolean;
}

const UploadTab: React.FC<UploadTabProps> = ({
  accept,
  hint,
  alertMessage,
  uploadFn,
  supportsOverwrite,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    const pending = fileList.filter(
      (f) => f.status !== 'done' && f.status !== 'error'
    );
    if (pending.length === 0) return;
    setUploading(true);

    for (let i = 0; i < pending.length; i++) {
      const file = pending[i];
      const originFile = file.originFileObj as File;
      if (!originFile) continue;

      try {
        await uploadFn(originFile);
        message.success(`${file.name} 上传成功`);
        setFileList((prev) =>
          prev.map((f) => (f.uid === file.uid ? { ...f, status: 'done' } : f))
        );
      } catch (err: unknown) {
        const axiosErr = err as {
          response?: { data?: { detail?: string; message?: string } };
          message?: string;
        };
        const detail = axiosErr.response?.data?.detail;
        const msg =
          axiosErr.response?.data?.message ??
          axiosErr.message ??
          '上传失败';

        if (supportsOverwrite && detail === 'CONFIRM_OVERWRITE') {
          // Wait for user confirmation
          const confirmed = await new Promise<boolean>((resolve) => {
            Modal.confirm({
              title: '覆盖确认',
              content: msg,
              okText: '确认覆盖',
              cancelText: '取消',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });

          if (confirmed) {
            try {
              await uploadFn(originFile, true);
              message.success(`${file.name} 上传成功（已覆盖）`);
              setFileList((prev) =>
                prev.map((f) =>
                  f.uid === file.uid ? { ...f, status: 'done' } : f
                )
              );
              continue;
            } catch (retryErr: unknown) {
              const re = retryErr as {
                response?: { data?: { message?: string } };
                message?: string;
              };
              message.error(
                `${file.name}: ${re.response?.data?.message ?? re.message ?? '重试失败'}`
              );
            }
          } else {
            message.info(`${file.name} 已取消`);
          }
        } else {
          message.error(`${file.name}: ${msg}`);
        }

        setFileList((prev) =>
          prev.map((f) =>
            f.uid === file.uid ? { ...f, status: 'error' } : f
          )
        );
      }
    }

    setUploading(false);
  };

  const hasPending = fileList.some(
    (f) => f.status !== 'done' && f.status !== 'error'
  );

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    fileList,
    accept,
    beforeUpload: (file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      const allowed = accept
        .split(',')
        .map((s) => s.trim().replace('.', '').toLowerCase());
      if (!allowed.includes(ext)) {
        message.error(`只支持 ${accept} 文件`);
        return Upload.LIST_IGNORE;
      }
      return false; // manual upload
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 24 }}>
      <Alert
        message={alertMessage}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Dragger {...uploadProps} disabled={uploading}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">点击或拖拽上传文件（支持多选）</p>
        <p className="ant-upload-hint">{hint}</p>
      </Dragger>
      {hasPending && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Space>
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              已选择 {fileList.length} 个文件
            </span>
            <Button
              type="primary"
              onClick={handleUpload}
              loading={uploading}
            >
              开始上传
            </Button>
            <Button
              onClick={() => setFileList([])}
              disabled={uploading}
            >
              清空
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

// ─── Upload functions (thin wrappers around apiClient) ───

const uploadSettlement = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return apiClient
    .post<UploadResponse>('/settlement/upload', fd)
    .then((r) => r.data);
};

const uploadMonthlyClearing = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return apiClient
    .post<UploadResponse>('/monthly-clearing/upload', fd)
    .then((r) => r.data);
};

const uploadMonthlySettlement = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return apiClient
    .post<UploadResponse>('/monthly-settlement/upload', fd)
    .then((r) => r.data);
};

const uploadStationAgc = (file: File, confirmOverwrite?: boolean) => {
  const fd = new FormData();
  fd.append('file', file);
  if (confirmOverwrite) {
    fd.append('confirm_overwrite', 'true');
  }
  return apiClient
    .post<UploadResponse>('/upload/station', fd)
    .then((r) => r.data);
};

declare global {
  interface Window {
    __OPS_MODE__?: boolean;
  }
}

// ─── Page ───

const DataUploadPage: React.FC = () => {
  const isOps = window.__OPS_MODE__ === true;

  const allTabs = [
    {
      key: 'daily',
      label: '日清分上传',
      children: (
        <UploadTab
          accept=".xlsx"
          hint="仅支持 .xlsx 格式"
          alertMessage="上传 Excel 结算单后，系统将自动解析并持久化至 station_settlement_data 数据库表。"
          uploadFn={uploadSettlement}
        />
      ),
    },
    {
      key: 'monthly-clear',
      label: '月清分上传',
      children: (
        <UploadTab
          accept=".xlsx"
          hint="仅支持 .xlsx 格式。一期/二期将自动合并至同一场站。"
          alertMessage="上传月清分 Excel 结算单（新能源市场化交易结算单），系统将自动解析日数据和追退补明细。重复上传同月同场站数据将覆盖旧数据。"
          uploadFn={uploadMonthlyClearing}
        />
      ),
    },
    {
      key: 'monthly-settle',
      label: '月结算上传',
      children: (
        <UploadTab
          accept=".pdf"
          hint="仅支持 .pdf 格式"
          alertMessage="上传国网光伏发电月度结算单 PDF。系统自动解析 Page 1 汇总数据入库，并用 Page 2 分期数据进行交叉验证。重复上传同月同场站将覆盖旧数据。"
          uploadFn={uploadMonthlySettlement}
        />
      ),
    },
    {
      key: 'agc',
      label: '场站AGC数据',
      children: (
        <UploadTab
          accept=".xlsx"
          hint="请上传包含 Sheet0(AGC) 和 Sheet1(考核) 的数据表，仅支持 .xlsx 格式"
          alertMessage="系统已开启储能功率正负号自动纠偏与主键冲突防御机制。"
          uploadFn={uploadStationAgc}
          supportsOverwrite
        />
      ),
    },
  ];

  const tabItems = isOps
    ? allTabs.filter((t) => t.key === 'agc')
    : allTabs;

  return (
    <PageContainer title={isOps ? '场站AGC数据上传' : '场站数据上传'}>
      <Tabs items={tabItems} />
    </PageContainer>
  );
};

export default DataUploadPage;
